/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file Popover component that allows the user to interact with the popover content.
 * @module Epic.AppOrchard.Core.AOTooltip
 */

import {
	Box,
	HTMLChakraProps,
	Placement,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverContentProps,
	PopoverTrigger,
	ThemingProps,
	useDisclosure,
	UseDisclosureReturn,
	UsePopoverProps,
} from "@chakra-ui/react";
import { aoLightGreen } from "ao/chakraTheme";
import React, { memo } from "react";

interface IProps {
	popoverContent: React.ReactNode;
	children: React.ReactNode;
	popoverStyles?: PopoverContentProps;
	popoverPlacement?: Placement | undefined;
	excludeArrow?: boolean;
	excludePopoverBody?: boolean;
	gutter?: number;
	customDisclosure?: UseDisclosureReturn;
}

export interface IPopoverWithoutChildrenProps
	extends HTMLChakraProps<"div">,
		ThemingProps<"Popover">,
		UsePopoverProps {}

type Props = IProps & IPopoverWithoutChildrenProps;

/**
 * Popover component that allows the user to interact with the popover content.
 */
export const PopoverWithHover = memo((props: Props) => {
	const {
		popoverContent,
		children,
		popoverStyles,
		popoverPlacement,
		excludeArrow,
		excludePopoverBody,
		gutter,
		customDisclosure,
		...rest
	} = props;

	const { isOpen, onOpen, onClose } = useDisclosure();
	const defaultTextColor = "black";

	const popoverBackgroundColor = popoverStyles?.bgColor || popoverStyles?.backgroundColor || aoLightGreen;

	const unifiedIsOpen = customDisclosure ? customDisclosure.isOpen : isOpen;
	const unifiedOnOpen = customDisclosure ? customDisclosure.onOpen : onOpen;
	const unifiedOnClose = customDisclosure ? customDisclosure.onClose : onClose;

	return (
		<Popover
			trigger="hover"
			placement={popoverPlacement || "bottom"}
			isOpen={unifiedIsOpen}
			gutter={gutter === undefined ? 8 : gutter}
			closeOnBlur={false}
			returnFocusOnClose={false}
		>
			<PopoverTrigger>
				<Box
					onFocus={unifiedOnOpen}
					onMouseEnter={unifiedOnOpen}
					onMouseLeave={unifiedOnClose}
					onBlur={unifiedOnClose}
					display={rest.display || "inline-block"}
					{...rest}
				>
					{children}
				</Box>
			</PopoverTrigger>
			<PopoverContent
				onFocus={unifiedOnOpen}
				onMouseEnter={unifiedOnOpen}
				onMouseLeave={unifiedOnClose}
				onBlur={unifiedOnClose}
				color={popoverStyles?.color || defaultTextColor}
				bgColor={popoverBackgroundColor}
				{...popoverStyles}
			>
				{!excludeArrow && <PopoverArrow bg={popoverBackgroundColor} />}
				{/**
				 * The following element has a negative margin top and custom padding top
				 * to pull empty space above the actual popover. This allows the user to
				 * change from hovering over the actual element (the Box above) to the
				 * popover with no physical gap where the mouse is hovering ove neither.
				 *
				 * The difference between the two (0.5rem) is the effective padding-top
				 * and was chosen to match the default padding of the popover body.
				 */}
				{excludePopoverBody ? (
					popoverContent
				) : (
					<PopoverBody mt="-0.8rem" pt="1.3rem">
						{popoverContent}
					</PopoverBody>
				)}
			</PopoverContent>
		</Popover>
	);
});
